import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// server/admin.js holds the real rule; this is the same pattern so a username that can never
// be an admin name — and never a piece of markup or a path — cannot leave the field.
export const ADMIN_USERNAME_MAX = 20;
const ADMIN_USERNAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}._-]{2,19}$/u;

export function adminUsernameError(value) {
  const typed = String(value).trim();
  if (!typed) return 'Type the admin username.';
  if (typed.length < 3) return 'The username is at least 3 characters.';
  if (typed.length > ADMIN_USERNAME_MAX) return `The username is at most ${ADMIN_USERNAME_MAX} characters.`;
  if (!ADMIN_USERNAME_PATTERN.test(typed)) {
    return 'Letters and numbers, with dot, underscore or hyphen inside. No spaces, slashes or other symbols.';
  }
  return '';
}

const AdminSessionContext = createContext(null);

export class AdminLoginError extends Error {
  constructor(message, code = '') {
    super(message);
    this.code = code;
  }
}

const SIGNED_OUT = { status: 'ready', authenticated: false, username: '' };

export function AdminSessionProvider({ children }) {
  const [session, setSession] = useState({ status: 'checking', authenticated: false, username: '' });

  const check = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/session', { credentials: 'same-origin' });
      const data = await response.json();
      setSession(
        response.ok && data.authenticated
          ? { status: 'ready', authenticated: true, username: data.username ?? '' }
          : SIGNED_OUT
      );
    } catch (error) {
      setSession(SIGNED_OUT);
    }
  }, []);

  const expire = useCallback(() => {
    setSession((current) => (current.authenticated ? SIGNED_OUT : current));
  }, []);

  // On load the cookie either still holds or it does not; asking the server is the only truth.
  useEffect(() => {
    check();
  }, [check]);

  const login = useCallback(async (username, password) => {
    const problem = adminUsernameError(username);
    if (problem) throw new AdminLoginError(problem, 'bad-admin-username-typed');
    if (!password) throw new AdminLoginError('Type the password too.', 'password-missing');

    let response;
    try {
      response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ username: username.trim(), password }),
      });
    } catch (error) {
      throw new AdminLoginError('Could not reach the server. Run `npm run server` and try again.', 'server-off');
    }

    const data = await response.json().catch(() => ({}));
    if (response.status === 401) {
      throw new AdminLoginError(data.error || 'Wrong admin username or password.', 'wrong-credentials');
    }
    if (!response.ok || !data.ok) {
      throw new AdminLoginError(data.error || `The server refused the sign in (${response.status}).`, data.code ?? 'failed');
    }

    setSession({ status: 'ready', authenticated: true, username: data.username ?? '' });
  }, []);

  const logout = useCallback(async () => {
    setSession(SIGNED_OUT);
    try {
      await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (error) {
      /* the cookie is already gone from the browser */
    }
  }, []);

  // Admin-only calls go through here: a 401 means the cookie died, so the desk locks itself again.
  const authFetch = useCallback(
    (url, options) =>
      fetch(url, { ...options, credentials: 'same-origin' }).then((response) => {
        if (response.status === 401) expire();
        return response;
      }),
    [expire]
  );

  const value = useMemo(
    () => ({ ...session, check, login, logout, expire, authFetch }),
    [session, check, login, logout, expire, authFetch]
  );

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
}

export function useAdminSession() {
  const context = useContext(AdminSessionContext);
  if (!context) throw new Error('useAdminSession has to be used inside AdminSessionProvider');
  return context;
}
